import { Link } from "react-router-dom";
import { ArrowRight, Plus } from "lucide-react";
import { PageHeader } from "@/shared/components/PageHeader";
import { Button } from "@/shared/components/ui/button";
import { Card } from "@/shared/components/ui/card";
import { EmptyState } from "@/shared/components/ui/empty-state";
import { useRecentDiscoveryRuns } from "@/features/campaigns/use-discovery";

/**
 * Discovery (global nav) — a cross-campaign ACTIVITY FEED (ADR-0023). A run is always
 * scoped to a campaign (that's where the prompt, tokens and screening are logged), so
 * this lists recent runs across every campaign — "what did we search, and what came
 * back" — each linking into its campaign's Discovery tab. Start a new search from here too.
 */
export function DiscoveryPage() {
  const { data, isLoading } = useRecentDiscoveryRuns();
  const runs = data ?? [];

  return (
    <div>
      <PageHeader
        title="Discovery"
        actions={
          <Button asChild>
            <Link to="/campaigns/new">
              <Plus className="h-[14px] w-[14px]" strokeWidth={2.4} />
              New campaign
            </Link>
          </Button>
        }
        footnote="Every discovery run is scoped to a campaign and logged with its prompt, tokens and screening. This is the recent activity across all your campaigns."
      />

      <p className="ge-eyebrow">RECENT RUNS</p>
      {!isLoading && runs.length === 0 ? (
        <EmptyState
          message="No discovery runs yet. Start a campaign and describe the market to run your first."
          action={
            <Button asChild>
              <Link to="/campaigns/new">New campaign</Link>
            </Button>
          }
        />
      ) : (
        <Card className="mt-3 divide-y divide-ge-border-row">
          {isLoading && <div className="px-4 py-3 text-[12px] text-ge-muted">Loading…</div>}
          {runs.map((r) => (
            <Link
              key={r.id}
              to={`/campaigns/${r.campaignId}/discovery`}
              data-testid={`discovery-run-${r.id}`}
              className="flex items-center gap-3 px-4 py-3 hover:bg-ge-card"
            >
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[13.5px] font-semibold text-ge-primary">{r.prompt}</span>
                <span className="block truncate text-[11.5px] text-ge-secondary">
                  {r.campaignName ?? "—"}
                </span>
              </span>
              {r.source && <span className="font-mono text-[10.5px] text-ge-faint">{r.source}</span>}
              <span className="font-mono text-[11px] text-ge-accent">{r.candidates ?? 0} found</span>
              <span className="font-mono text-[10.5px] text-ge-muted">
                {new Date(r.createdAt).toLocaleDateString()}
              </span>
              <ArrowRight className="h-[13px] w-[13px] text-ge-secondary" strokeWidth={2} />
            </Link>
          ))}
        </Card>
      )}
    </div>
  );
}
